import React, { useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, LayoutDashboard, Lock, ChevronDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Travel", to: "/travel" },
  { label: "Fashion", to: "/fashion" },
  { label: "Self-Care", to: "/self-care" },
  { label: "Lifestyle", to: "/lifestyle" },
  { label: "About", to: "/about" },
];

const SHOP = [
  { label: "Temu Center", to: "/temu" },
  { label: "Shein Center", to: "/shein" },
];

export const Navbar = () => {
  const { user, setAuthOpen, setPlannerOpen, logout, setAdminGateOpen } = useApp();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const clicks = useRef([]);

  const handleLogo = () => {
    const now = Date.now();
    clicks.current = [...clicks.current.filter((t) => now - t < 800), now];
    if (clicks.current.length >= 3) {
      clicks.current = [];
      setAdminGateOpen(true);
      return;
    }
    navigate("/");
  };

  return (
    <header className="glass border-b border-[rgba(212,175,55,0.14)]" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-2">
            <button onClick={handleLogo} className="flex flex-col leading-none text-left" data-testid="logo-link" title="MOVA (triple-tap for admin)">
              <span className="font-accent text-2xl text-champagne">MOVA</span>
              <span className="hidden sm:block text-[9px] tracking-[0.3em] text-amber-400/70 mt-1">PLAN · SHOP · TRAVEL · SELF-CARE · LIVE</span>
            </button>
            <button onClick={() => setAdminGateOpen(true)} data-testid="admin-lock-btn" title="Admin access" className="opacity-30 hover:opacity-100 text-amber-300 transition-opacity duration-200">
              <Lock size={13} />
            </button>
          </div>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`} className={`text-sm tracking-wide transition-colors duration-200 hover:text-champagne ${location.pathname === l.to ? "text-champagne" : "text-slate-300"}`}>
                {l.label}
              </Link>
            ))}
            <div className="relative group">
              <button className="flex items-center gap-1 text-sm tracking-wide text-slate-300 hover:text-champagne transition-colors duration-200" data-testid="nav-shop">Shop <ChevronDown size={14} /></button>
              <div className="absolute right-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="w-44 rounded-xl glass gold-border p-2">
                  {SHOP.map((s) => (
                    <Link key={s.to} to={s.to} data-testid={`nav-${s.to.slice(1)}`} className="block px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-amber-400/10 hover:text-champagne transition-colors">{s.label}</Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => setPlannerOpen(true)} data-testid="nav-planner-btn" className="flex items-center gap-2 text-sm text-amber-200 hover:text-champagne transition-colors duration-200">
              <Sparkles size={16} /> AI Planner
            </button>
            {user ? (
              <div className="flex items-center gap-3">
                <button onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn" className="flex items-center gap-2 text-sm text-slate-200 hover:text-champagne transition-colors duration-200">
                  <LayoutDashboard size={16} /> Dashboard
                </button>
                <img src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || "M")}`} alt={user.name} onClick={logout} title="Click to sign out" className="w-9 h-9 rounded-full object-cover border border-amber-400/40 cursor-pointer" data-testid="nav-avatar" />
              </div>
            ) : (
              <Button onClick={() => setAuthOpen(true)} data-testid="nav-signin-btn" className="rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] hover:opacity-90 font-medium px-6">Sign In</Button>
            )}
          </div>

          <button className="lg:hidden text-champagne" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-[rgba(212,175,55,0.14)] px-6 py-6 space-y-4" data-testid="mobile-menu">
          {[...LINKS, ...SHOP].map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-slate-200 hover:text-champagne">{l.label}</Link>
          ))}
          <button onClick={() => { setPlannerOpen(true); setOpen(false); }} className="flex items-center gap-2 text-amber-200"><Sparkles size={16} /> AI Planner</button>
          {user ? (
            <div className="flex flex-col gap-3">
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-slate-200">My Dashboard</Link>
              <button onClick={logout} className="text-left text-slate-400">Sign out</button>
            </div>
          ) : (
            <Button onClick={() => { setAuthOpen(true); setOpen(false); }} className="rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] w-full">Sign In</Button>
          )}
        </div>
      )}
    </header>
  );
};
